import MarkerClusterer from "@googlemaps/markerclustererplus";
import { Api, MapSettings, StringUtil, UserSettings } from "@mcleod/core";
import { getSVGUrl } from "@mcleod/images";
import { Snackbar } from "../../";
import { ReflectiveSnackbars } from "../../base/ReflectiveSnackbars";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { DistanceVendorInfo } from "../../services/DistanceProfileService";
import { Panel } from "../panel/Panel";
import { Map, VendorMap } from "./Map";
import { MapPin } from "./MapPin";
import { MapVendor, VendorMapProps } from "./MapProps";
import { MapRouteToggleButtonsGoogle, RouteToggleOptions } from "./MapRouteToggleButtonsGoogle";

const log = DesignableObjectLogManager.getLogger("components.map");

interface google {
    maps: typeof window.google.maps,
}

interface Marker {
    marker: google.maps.marker.AdvancedMarkerElement;
    type: RouteType;
}

interface GoogleRouteUserSettings {
    show_google_route?: string;
}

export enum RouteType {
    Google,
    Trimble,  // This includes both PC*Miler On Prem and PC*Miler Web (owned by Trimble)
    MileMaker
}

export class GoogleMap extends Panel implements VendorMap {

    // State for toggling route visibility
    private routeVisibility = {
        google: true,
        trimble: true,  // Trimble includes both PC*Miler On Prem and PC*Miler Web
        milemaker: true
    };

    map: google.maps.Map;
    markerClusterer: MarkerClusterer;
    markers: google.maps.Marker[];
    currentCircle: google.maps.Circle;
    parentMap: Map;
    directionsService: google.maps.DirectionsService;
    directionsRenderer: google.maps.DirectionsRenderer;
    trimblePath: google.maps.Polyline;
    milemakerPath: google.maps.Polyline;
    pcmilerOnPremPath: google.maps.Polyline;
    routesAPIPath: google.maps.Polyline;
    advanceMarkers: Marker[];
    private trimbleRouteMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
    private milemakerRouteMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

    private trafficLayer: google.maps.TrafficLayer;
    get googleTrafficLayer(): google.maps.TrafficLayer { return this.trafficLayer; }
    private _userSelectionMapType = UserSettings.get()?.google_map_type;
    private _userSelectionMapLayer = UserSettings.get()?.google_map_layer;
    private static scriptLoadPromise: Promise<void>;
    private isMapInitialized: boolean = false;
    private disableDefaultUI: boolean = false;
    private proposedRouteData: any[] | null = null;
    private routeToggleButtons: MapRouteToggleButtonsGoogle | null = null;
    private distanceVendorInfo: DistanceVendorInfo | null = null;
    private disableToggleButtons: boolean = true;

    constructor(props?: Partial<VendorMapProps>) {
        super({borderRadius: 4, ...props});
        if (props.parentMap == null)
            throw new Error("GoogleMap needs to have its parentMap property set.");
        this.markers = [];
        this.advanceMarkers = [];
        this.parentMap.vendor = MapVendor.GOOGLE;
        this.disableDefaultUI = props?.disableDefaultUI;
    }

    setCenter(latitude: number, longitude: number) {
        this.map.setCenter(new google.maps.LatLng(latitude, -longitude));
    }

    getCenter(): [number, number] {
        const latLng: google.maps.LatLng = this.map.getCenter();
        return [latLng.lat(), latLng.lng()];
    }

    public async ensureLoaded(): Promise<void> {
        try {
            const goolgePlacesApiKey = MapSettings.get().google_api_key;
            if (StringUtil.isEmptyString(goolgePlacesApiKey)) {
                const snackbarProps = { customBackground: 'error.lighter', millisUntilDismissal: 10000 }
                ReflectiveSnackbars.showSnackbar("No API key found for Google. Map cannot be shown", snackbarProps);
                return;
            }
            if (window.google == null) {
                if (GoogleMap.scriptLoadPromise == null) {
                    GoogleMap.scriptLoadPromise = this.initHeader();
                }
            }
            await GoogleMap.scriptLoadPromise;
            this.initMap();

        } catch (error) {
            log.error("Error loading GoogleMap", error);
        }
    }

    addPin(pin: MapPin): void {
        if (this.map == null)
            return;
        const needsClickListener = this.parentMap.pinNeedsClickListener(pin);
        const labelOrigin = pin.image != null && pin.image != "map-pin-cluster" ? new google.maps.Point(12, 12) : null;
        const image = pin.image == null ? undefined : {
            icon: {
                url: getSVGUrl(pin.image),
                labelOrigin: labelOrigin
            }
        };
        const marker = new window.google.maps.Marker({
            ...image,
            position: new window.google.maps.LatLng(pin.latitude, pin.longitude),
            label: {
                text: pin.caption,
                color: pin.captionColor,
                fontSize: "12px"
            },
            title: pin.tooltip,
            draggable: pin.draggable,
            cursor: needsClickListener ? "pointer" : undefined
        });

        this.markers.push(marker);
        if (pin.draggable) {
            marker.addListener("dragend", (event) =>
                {
                    pin.endLatitude = event.latLng.lat();
                    pin.endLongitude = event.latLng.lng();
                    this.parentMap.dragEnd(pin, event)
                }
            );
        }
        if (needsClickListener)
            marker.addListener("click", (event) => this.parentMap.pinClicked(pin, event.domEvent));
        if (pin.clusterable === true)
            this.markerClusterer.addMarker(marker);
        else
            marker.setMap(this.map);
    }

    focusOnPin(pin: MapPin, mapPinIndex: number) {

    }

    defocusOnPin(pin: MapPin, mapPinIndex: number) {

    }

    removeAllPins() {
        this.markerClusterer?.removeMarkers(this.markers);
        for (const marker of this.markers)
            marker.setMap(null);
        this.markers = [];
    }

    private async initHeader(): Promise<void> {
        const mapSettings = MapSettings.get();
        const url = mapSettings.google_maps_url;
        const apiKey = mapSettings.google_api_key;
        const version = mapSettings.version;
        const callbackName = "__mcleodGoogleMapsLoaded";

        return new Promise((resolve, reject) => {
            // With `loading=async`, script.onload may fire before the Maps API is fully ready.
            // Using the documented callback mechanism so we only resolve when Google calls us.
            (window as any)[callbackName] = () => {
                try {
                    delete (window as any)[callbackName];
                } catch {
                    // ignore
                }
                log.debug(this, "Map initialized successfully");
                resolve();
            };

            const script = document.createElement("script");
            script.async = true;
            script.defer = true;
            script.src = url + apiKey + "&v=" + version + "&callback=" + callbackName;
            script.onerror = () => {
                try {
                    delete (window as any)[callbackName];
                } catch {
                    // ignore
                }
                log.error(this, "An error occurred during map initialization");
                reject();
            };
            window.document.body.appendChild(script);
        });
    }

    private getGestureHandling() {
        if (MapSettings.get()?.google_greedy)
            return "greedy";
        else
            return "cooperative";
    }

    private setMapType() {
        const [MapTypeStandard, MapTypeSatellite, MapTypeTerrain, MapTypeStandardPlus] = ['A', 'S', 'T', 'P'];
        const mapTypes = {
            [MapTypeStandard]: 'roadmap',
            [MapTypeSatellite]: 'satellite',
            [MapTypeTerrain]: 'terrain',
            [MapTypeStandardPlus]: 'hybrid'
        };
        const userSelectionMapType = this._userSelectionMapType;
        return mapTypes[userSelectionMapType] || 'roadmap';
    }

    private initMap() {
        if (this.isMapInitialized)
            return;
        this.isMapInitialized = true
        const userSelectionMapType = this.setMapType();

        this.map = new window.google.maps.Map(this._element, {
            disableDefaultUI: this.disableDefaultUI,
            gestureHandling: this.getGestureHandling(),
            center: new window.google.maps.LatLng(39.8283, -98.5795),
            zoom: 5,
            mapTypeId: userSelectionMapType,
            mapId: Math.random().toString()
        });

        log.debug(this, 'Google Maps API version: ' + google.maps.version);

        this.markerClusterer = new MarkerClusterer(this.map, [], {
            styles: [
                {
                    textColor: "white",
                    anchorText: [18, 0],
                    fontWeight: "600",
                    url: getSVGUrl("map-pin-cluster"),
                    height: 48,
                    width: 48
                }
            ],

            maxZoom: 12,
            gridSize: 20
        });
        this.directionsService = new google.maps.DirectionsService();
        this.directionsRenderer = new google.maps.DirectionsRenderer();

        this.initTrafficLayer();
        this.applyUserSettingForTrafficLayer();
        if(!this.disableDefaultUI) {
            this.setupTrafficToggleButton();
        }
        this.setupFullscreenListener();

        this.routeVisibility = {
            google: true,
            trimble: true,
            milemaker: true
        };

        // Add a map idle event listener to ensure toggles get created after map is ready
        this.map.addListener('idle', () => {
            // Try to create toggle buttons if they don't exist yet
            this.ensureRouteToggleButtonsCreated();
        });
    }

    private ensureRouteToggleButtonsCreated(routeConfig?: any): void {
        // Skip if toggle buttons already exist
        if (this.routeToggleButtons) {
            return;
        }

        // Skip if UI is disabled or toggle buttons should be disabled
        if (this.disableDefaultUI || this.disableToggleButtons) {
            log.debug('GoogleMap: Toggle buttons disabled, not creating');
            return;
        }

        // Skip if map isn't initialized yet
        if (!this.map || !this.map.getDiv()) {
            log.debug('GoogleMap: Map not ready for toggle buttons');
            return;
        }

        const options = this.getRouteToggleOptions(null, null, null, routeConfig);

        // Create toggle buttons if appropriate
        if (options.showGoogle || options.showMileMaker || options.showTrimble) {
            try {
                const mapDiv = this.map.getDiv();
                log.debug('GoogleMap: Creating route toggle buttons');

                this.routeToggleButtons = new MapRouteToggleButtonsGoogle(
                    mapDiv,
                    this.routeVisibility,
                    {
                        google: (visible) => this.setGoogleRouteVisibility(visible),
                        milemaker: (visible) => this.setMileMakerRouteVisibility(visible),
                        trimble: (visible) => this.setTrimbleRouteVisibility(visible),
                        handleRouteToggle: (rd) => this.handleMileMakerRouteToggle(rd),
                        handleTrimbleRouteToggle: (rd) => this.handleTrimbleRouteToggle(rd)
                    }
                );
                this.routeToggleButtons.render(options);
                this.routeToggleButtons.reappendOnFullscreen();

                log.debug('GoogleMap: Route toggle buttons created successfully');
            } catch (error) {
                log.error('GoogleMap: Error creating route toggle buttons:', error);
            }
        }
    }

    private initTrafficLayer(): void {
        this.trafficLayer = new google.maps.TrafficLayer();
    }

    private applyUserSettingForTrafficLayer(): void {
        if ("Y" === this._userSelectionMapLayer) {
            this.trafficLayer.setMap(this.map);
        } else {
            this.trafficLayer.setMap(null);
        }
    }

    private setupTrafficToggleButton(): void {
        const toggleBtn = document.createElement('button');
        toggleBtn.style.backgroundColor = '#fff';
        toggleBtn.style.border = '2px solid #fff';
        toggleBtn.style.borderRadius = '3px';
        toggleBtn.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
        toggleBtn.style.color = 'rgb(25,25,25)';
        toggleBtn.style.cursor = 'pointer';
        toggleBtn.style.fontFamily = 'Roboto,Arial,sans-serif';
        toggleBtn.style.fontSize = '16px';
        toggleBtn.style.lineHeight = '38px';
        toggleBtn.style.margin = '20px 0 22px';
        toggleBtn.style.padding = '0 5px';
        toggleBtn.style.textAlign = 'center';
        toggleBtn.textContent = 'Toggle Traffic';
        toggleBtn.onclick = () => {
            if (this.trafficLayer.getMap()) {
                this.trafficLayer.setMap(null);
            } else {
                this.trafficLayer.setMap(this.map);
            }
        };
        this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(toggleBtn);
    }

    private updateGoogleRouteVisibility(): void {
        if (this.directionsRenderer) {
            this.directionsRenderer.setMap(this.routeVisibility.google ? this.map : null);
        }
        if (this.routesAPIPath) {
            this.routesAPIPath.setVisible(this.routeVisibility.google);
        }
    }

    fitPins() {
        if (this.map == null)
            return;
        if (this.markers.length === 1)
            this.map.setCenter(this.markers[0].getPosition());
        else if (this.markers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            for (const marker of this.markers)
                bounds.extend(marker.getPosition());
            this.map.fitBounds(bounds);
        }
        if (this.map.getZoom() > 10)
            this.map.setZoom(10);
    }

    setZoom(level: number) {
        this.map.setZoom(level);
    }

    getZoom(): number {
        return this.map.getZoom();
    }

    private getDefaultShowGoogleRoute(): boolean {
        return (UserSettings.get() as GoogleRouteUserSettings | undefined)?.show_google_route === "Y";
    }

    setStopZoomLevel: (isTrafficEnabled: string, routeData: any[]) => void;

    createRoute(routeData: any[], routeType: RouteType, hideMarkers?: boolean | false, routeId?: string, routeColor?: string, showProposedRoute?: boolean, disableToggleButtons?: boolean): void {
        // Check if routeId contains vendor info
        let actualRouteId = routeId;
        let routeConfig = null;

        try {
            if (routeId && routeId.startsWith('{') && routeId.includes('vendorInfo')) {
                const routeData = JSON.parse(routeId);
                actualRouteId = routeData.routeId;
                // Extract and set vendor info
                if (routeData.vendorInfo) {
                    this.distanceVendorInfo = routeData.vendorInfo;
                    // Extract route configuration if available
                    if (routeData.vendorInfo.routeConfig) {
                        routeConfig = routeData.vendorInfo.routeConfig;
                    }
                    log.debug('GoogleMap: Extracted vendor info from routeId', this.distanceVendorInfo);
                }
            }
        } catch (e) {
            log.error('GoogleMap: Error parsing routeId for vendor info', e);
        }

        // Continue with the regular createRoute logic using actualRouteId
        this.clearRoute(actualRouteId, showProposedRoute);
        const useDirectionsAPI = MapSettings.get().use_legacy_google_api;
        const effectiveShowGoogleRoute = this.getDefaultShowGoogleRoute();

        this.disableToggleButtons = disableToggleButtons === true;

        // Set initial visibility states based on parameters
        if (routeType === RouteType.Google) {
            this.routeVisibility.google = effectiveShowGoogleRoute;
        }

        if (routeType == RouteType.Google) {
            if (effectiveShowGoogleRoute) {
                const data: google.maps.LatLngLiteral[] = this.formatGoogleRouteData(routeData);
                if (useDirectionsAPI) {
                    this.setupDirectionsAPIRequest(hideMarkers, data, routeColor);

                    // Set initial visibility based on showGoogleRoute
                    if (this.directionsRenderer) {
                        this.directionsRenderer.setMap(this.routeVisibility.google ? this.map : null);
                    }
                } else {
                    this.setupRoutesAPIRequest(data, routeColor);
                    if (!hideMarkers) {
                        this.addMarkers(data, effectiveShowGoogleRoute, RouteType.Google);
                    }

                    // Set initial visibility for RoutesAPI path
                    if (this.routesAPIPath) {
                        this.routesAPIPath.setVisible(this.routeVisibility.google);
                    }
                }
            } else {
                log.debug('GoogleMap: Skipping Google route creation because show_google_route is disabled');
            }
        }
        if (routeType == RouteType.Trimble) {
            if (this.trimblePath) {
                this.trimblePath.setMap(null);
            }
            const trimbleColor = routeColor || MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.TRIMBLE);
            const trimbleCoordinateData = Array.isArray(routeData?.["trimble_coordinate_data"])
                ? routeData["trimble_coordinate_data"]
                : routeData;
            this.trimblePath = new google.maps.Polyline({
                path: trimbleCoordinateData,
                geodesic: true,
                strokeColor: trimbleColor,
                strokeOpacity: 1,
                strokeWeight: 4,
                zIndex: useDirectionsAPI ? null : 1
            });

            // Set the Trimble path to be visible based on showProposedRoute (similar to MileMaker)
            // This ensures the path's visibility state matches the toggle button state
            const trimbleVisible = showProposedRoute !== undefined ? !!showProposedRoute : false;
            log.debug('Creating Trimble route, setting visibility:', trimbleVisible, 'showProposedRoute:', showProposedRoute);

            // Update the visibility state in routeVisibility object to match
            if (!this.routeVisibility) {
                this.routeVisibility = {
                    google: false,
                    trimble: trimbleVisible,
                    milemaker: false
                };
            } else {
                this.routeVisibility.trimble = trimbleVisible;
            }

            // Set the map display property
            this.trimblePath.setMap(trimbleVisible ? this.map : null);

            if (showProposedRoute) {
                this.proposedRouteData = trimbleCoordinateData;
            }

            if (trimbleVisible && !hideMarkers) {
                const trimbleStops = Array.isArray(routeData?.["route_stop_coords"])
                    ? routeData["route_stop_coords"]
                    : routeData;
                this.addMarkers(trimbleStops, true, RouteType.Trimble);
            }
        }
        if (routeType == RouteType.MileMaker) {
            if (this.milemakerPath) {
                this.milemakerPath.setMap(null);
            }
            const milemakerRouteData = routeData['milemaker_polyline_data'];
            const milemakerColor = routeColor || MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.MILEMAKER);
            this.milemakerPath = new google.maps.Polyline({
                path: milemakerRouteData,
                geodesic: true,
                strokeColor: milemakerColor,
                strokeOpacity: 1.0,
                strokeWeight: 4,
                zIndex: useDirectionsAPI ? null : 1
            });

            // Set the MileMaker path to be visible based on showProposedRoute
            // This ensures the path's visibility state matches the toggle button state
            const milemakerVisible = showProposedRoute !== undefined ? !!showProposedRoute : false;
            log.debug('Creating MileMaker route, setting visibility:', milemakerVisible, 'showProposedRoute:', showProposedRoute);

            // Update the visibility state in routeVisibility object to match
            if (!this.routeVisibility) {
                this.routeVisibility = {
                    google: false,
                    trimble: true,
                    milemaker: milemakerVisible
                };
            } else {
                this.routeVisibility.milemaker = milemakerVisible;
            }

            // Set the map display property
            this.milemakerPath.setMap(milemakerVisible ? this.map : null);

            if (showProposedRoute) {
                this.proposedRouteData = milemakerRouteData;
            }

            if (milemakerVisible && !hideMarkers) {
                const routeWithoutPoints = routeData.filter(d => d != typeof Array);
                this.addMarkers(routeWithoutPoints, true, RouteType.MileMaker);
            }
        }

        // Only handle toggle buttons if they're not disabled
        if (!this.disableToggleButtons) {
            if (this.routeToggleButtons) {
                // Create new options now that milemakerPath exists
                const updatedOptions = this.getRouteToggleOptions(routeData, showProposedRoute, effectiveShowGoogleRoute, routeConfig);
                this.routeToggleButtons.updateOptions(updatedOptions);
            } else {
                this.ensureRouteToggleButtonsCreated(routeConfig);

                // Try again with updated options
                if (this.routeToggleButtons) {
                    const updatedOptions = this.getRouteToggleOptions(routeData, showProposedRoute, effectiveShowGoogleRoute, routeConfig);
                    this.routeToggleButtons.updateOptions(updatedOptions);
                }
            }
        }
    }

    createRouteFromCoordArray(routeData: any[], routetype?: any, hideMarkers?: boolean | false) {
        // Not yet implemented for Google Maps
    }

    private setupDirectionsAPIRequest(hideMarkers: boolean, data: google.maps.LatLngLiteral[], routeColor: string) {
        const color = routeColor || MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.GOOGLE);
        let waypoints: google.maps.DirectionsWaypoint[];
        if (hideMarkers) {
            waypoints = data.slice(1, -1).map(point => ({
                location: point,
                stopover: false // Prevent waypoints from being displayed as markers
            })) as google.maps.DirectionsWaypoint[];
        } else {
            waypoints = data.slice(1, -1) as google.maps.DirectionsWaypoint[];
        }
        log.debug(this, "Google Directions API Request");
        this.directionsService
            .route({
                origin: data[0]["location"],
                destination: data[data.length - 1]["location"],
                waypoints: waypoints,
                travelMode: google.maps.TravelMode.DRIVING
            })
            .then((response) => {
                log.debug(this, "Google Directions API Response", response);
                this.directionsRenderer.setDirections(response);
                if (hideMarkers) {
                    this.directionsRenderer.setOptions({
                        suppressMarkers: true // Prevent origin and destination markers from displaying
                    });
                }
                this.directionsRenderer.setMap(this.routeVisibility.google ? this.map : null);
                this.updateGoogleRouteVisibility();
            });
    }

    private async setupRoutesAPIRequest(data: google.maps.LatLngLiteral[], routeColor: string) {
        const color = routeColor || MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.GOOGLE);
        const endpoint = "https://routes.googleapis.com/directions/v2:computeRoutes";
        const headers = {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": MapSettings.get().google_api_key,
            "X-Goog-FieldMask": "routes.polyline.encodedPolyline"
        }

        // Validate and filter out invalid stops
        const validData = data.filter(stop => {
            if (!stop || !stop["location"] || typeof stop["location"].lat !== "number" || typeof stop["location"].lng !== "number" || isNaN(stop["location"].lat) || isNaN(stop["location"].lng)) {
                log.error("Invalid stop in route data, skipping:", stop);
                return false;
            }
            return true;
        });
        if (validData.length < 2) {
            // log.error("Not enough valid stops to create a route.");
            // Snackbar.showWarningSnackbar("Not enough valid stops to create a route.");
            return;
        }
        const origin = validData[0];
        const destination = validData[validData.length-1];
        const stops = [];

        // Add intermediate stops if they are present
        if (validData.length > 2) {
            // Do not include the Origin and Destination
            const intermediateStops = validData.slice(1, -1);
            intermediateStops.forEach(stop => {
                stops.push(`{ "latLng": { "latitude": ${stop['location'].lat}, "longitude": ${Math.abs(stop['location'].lng) * -1} } }`);
            });
            log.debug(this, "Intermediate Stops: " + stops);
        }

        const request = {
            "origin" : {
                "location" : {
                    latLng: {
                        latitude: origin["location"].lat,
                        longitude: origin["location"].lng
                    }
                }
            },
            destination: {
                location: {
                    latLng: {
                        latitude: destination["location"].lat,
                        longitude: destination["location"].lng
                    }
                }
            },
            intermediates: stops.map(stop => ({
                location: JSON.parse(stop),
            })),
            travelMode: "DRIVE",
            routingPreference: "TRAFFIC_UNAWARE",
            computeAlternativeRoutes: false,
            routeModifiers: {
                avoidTolls: false,
                avoidHighways: false,
                avoidFerries: false
            },
            languageCode: "en-US",
            units: "IMPERIAL"
        }

        log.debug(this, "Google Routes API Request", endpoint, JSON.stringify(request));
        const resp = await fetch(endpoint, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(request)
        })

        const obj = await resp.json();
        log.debug(this, "Google Routes API Response", endpoint, obj);

        const msg = "Google Routes could not find a route.";
        if (!resp.ok) {
            log.error(this, "Google Routes API request failed.  " + obj.error.message + "\n\nCode:  " + obj.error.code + "\nStatus: " + obj.error.status);
            Snackbar.showWarningSnackbar(msg);
            return;
        }

        await this.processGoogleRouteResponse(obj, routeColor, msg);
    }

    private async processGoogleRouteResponse(obj: any, routeColor: string, msg: string) {
        if (obj.routes && obj.routes.length > 0 && obj.routes[0].polyline) {
            const encodedPolyline = obj.routes[0].polyline.encodedPolyline;
            log.debug('GoogleMap: Decoding polyline of type:', typeof encodedPolyline);

            try {
                // Wait for the decoded polyline
                const decodedPolyline = await this.decodePolyline(encodedPolyline);
                log.debug('GoogleMap: Polyline decoded successfully, points:', decodedPolyline.length);

                this.addRouteLineAndFitMapBounds(decodedPolyline, routeColor);
            } catch (error) {
                log.error('GoogleMap: Error decoding polyline:', error);
                Snackbar.showWarningSnackbar("Error processing route data");
            }
        } else {
            log.debug(this, "Google Routes API Response did not return a route.");
            Snackbar.showWarningSnackbar(msg);
        }
    }

    private addRouteLineAndFitMapBounds(decodedPolyline: google.maps.LatLng[], routeColor: string) {
        // If a specific color is provided, use it
        // Otherwise get the appropriate color from settings based on map vendor (google) and route vendor
        const color = routeColor || MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.GOOGLE);

        if (this.routesAPIPath) {
            this.routesAPIPath.setMap(null);
        }
        this.routesAPIPath = new google.maps.Polyline({
            path: decodedPolyline,
            geodesic: true,
            strokeColor: color,
            strokeOpacity: 0.5,
            strokeWeight: 8,
            visible: this.routeVisibility.google
        });

        this.routesAPIPath.setMap(this.map);
        this.addMapBounds(decodedPolyline);
        this.updateGoogleRouteVisibility();
    }

    private async decodePolyline(polyline: any) {
        if (!polyline || typeof polyline !== 'string') {
            log.error('GoogleMap: Invalid polyline to decode:', polyline);
            return [];
        }

        try {
            // Ensure the geometry library is loaded
            if (!google.maps.geometry) {
                log.debug('GoogleMap: Loading geometry library for polyline decoding');
                await google.maps.importLibrary("geometry") as google.maps.GeometryLibrary;
            }

            log.debug('GoogleMap: Decoding polyline with encoding, length:', polyline.length);

            return google.maps.geometry.encoding.decodePath(polyline);
        } catch (error) {
            log.error('GoogleMap: Error decoding polyline with Google Maps API:', error);

            // Implement basic fallback decoding algorithm if Google's fails
            try {
                log.debug('GoogleMap: Attempting fallback polyline decoding');
                return this.fallbackDecodePolyline(polyline);
            } catch (fallbackError) {
                log.error('GoogleMap: Fallback decoding also failed:', fallbackError);
                // Return empty array if all decoding fails to avoid breaking the app
                return [];
            }
        }
    }

    /**
     * Fallback implementation of Google's polyline decoding algorithm
     * Based on https://developers.google.com/maps/documentation/utilities/polylinealgorithm
     */
    private fallbackDecodePolyline(encoded: string): google.maps.LatLng[] {
        const points: google.maps.LatLng[] = [];
        let index = 0;
        const len = encoded.length;
        let lat = 0;
        let lng = 0;

        log.debug('GoogleMap: Starting fallback polyline decode, length:', len);

        try {
            while (index < len) {
                let b;
                let shift = 0;
                let result = 0;

                do {
                    b = encoded.charCodeAt(index++) - 63;
                    result |= (b & 0x1f) << shift;
                    shift += 5;
                } while (b >= 0x20);

                const dlat = ((result & 1) !== 0 ? ~(result >> 1) : (result >> 1));
                lat += dlat;

                shift = 0;
                result = 0;

                do {
                    b = encoded.charCodeAt(index++) - 63;
                    result |= (b & 0x1f) << shift;
                    shift += 5;
                } while (b >= 0x20);

                const dlng = ((result & 1) !== 0 ? ~(result >> 1) : (result >> 1));
                lng += dlng;

                // Convert to LatLng object
                points.push(new google.maps.LatLng(lat * 1e-5, lng * 1e-5));
            }

            log.debug('GoogleMap: Fallback decoding successful, decoded points:', points.length);
            return points;
        } catch (e) {
            log.error('GoogleMap: Error in fallback polyline decoding:', e);
            throw e;
        }
    }

    async addMarkers(data: google.maps.LatLngLiteral[] | any, isVisible: boolean, routeType: RouteType) {
        if (!Array.isArray(data)) {
            log.debug('GoogleMap: Skipping route markers because marker data is missing or invalid', data);
            return;
        }

        const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
        const isGoogleRoute = routeType === RouteType.Google;
        // Create custom glyph text starting at upper case 'A'
        let letter = 65; // ASCII 'A'
        data.forEach(stop => {
            const pinTextGlyph = new PinElement({
                glyph: String.fromCharCode(letter++),
                glyphColor: 'white',
                background: isGoogleRoute ? '#4285F4' : '#EA4335',
                borderColor: isGoogleRoute ? '#1967D2' : '#C5221F'
            });

            const lat = isGoogleRoute ? Number(stop.location.lat) : Number(stop.latitude) || Number(stop.lat);
            const lng = Number(Math.abs(isGoogleRoute ? stop.location.lng : stop.longitude || stop.lon) * -1);
            const position = { lat, lng };

            const marker = new AdvancedMarkerElement({
                map: isVisible ? this.map : null,
                position: position,
                content: pinTextGlyph.element
           });

           this.advanceMarkers.push({ marker, type: routeType });
           if (routeType === RouteType.Trimble) {
               this.trimbleRouteMarkers.push(marker);
           } else if (routeType === RouteType.MileMaker) {
               this.milemakerRouteMarkers.push(marker);
           }
        });
    }

    addMapBounds(decodedPoly: google.maps.LatLng[]) {
        const bounds = new google.maps.LatLngBounds();
        // Use array of coords
        for (let i = 0; i < decodedPoly.length; i++) {
            bounds.extend(decodedPoly[i]);
        }
        this.map.fitBounds(bounds);
    }

    clearRoute(routeId?: string, showProposedRoute?: boolean) {
        if (routeId) {
            return;
        }
        // Remove route toggle container if it exists
        const container = document.getElementById('route-toggle-container');
        if (container && container.parentNode) {
            container.parentNode.removeChild(container);
        }

        this.routeVisibility.milemaker = !!showProposedRoute;
        this.routeVisibility.google = true;
        this.routeVisibility.trimble = true;
        this.proposedRouteData = null;

        // Remove Google Directions route
        if (this.directionsRenderer) {
            this.directionsRenderer.setMap(null);
        }
        // Remove Google Polyline route
        if (this.routesAPIPath) {
            this.routesAPIPath.setMap(null);
            this.routesAPIPath = null;
        }
        // Remove Trimble route
        if (this.trimblePath) {
            this.trimblePath.setMap(null);
            this.trimblePath = null;
        }
        // Remove MileMaker route
        if (this.milemakerPath) {
            this.milemakerPath.setMap(null);
            this.milemakerPath = null;
        }
        // Remove PC*Miler On Prem route (part of Trimble)
        if (this.pcmilerOnPremPath) {
            this.pcmilerOnPremPath.setMap(null);
            this.pcmilerOnPremPath = null;
        }
        // Remove advanced markers
        this.advanceMarkers.forEach(am => {
            am.marker.map = null;
        });
        this.advanceMarkers = [];
        this.trimbleRouteMarkers = [];
        this.milemakerRouteMarkers = [];
        if (this.routeToggleButtons) {
            this.routeToggleButtons.destroy();
            this.routeToggleButtons = null;
        }
        // Ensure Google route is hidden if toggled off
        this.updateGoogleRouteVisibility();
    }

    private formatGoogleRouteData(routeData: any[]): google.maps.LatLngLiteral[] {
        const coordinatesList = [];
        routeData.forEach(stop => {
            const lat = Number(stop.latitude);
            const lng = Number(Math.abs(stop.longitude) * -1);
            if (isNaN(lat) || isNaN(lng)) {
                log.error('Invalid stop coordinates, skipping:', stop);
                return;
            }
            coordinatesList.push({"location": {lat, lng}});
        });
        return coordinatesList;
    }

    showCircle(latitude: number, longitude: number, radius: number) {
        if (this.map == null)
            return;

        this.removeCircle();
        radius = radius * 1609.34;

        const circle = new window.google.maps.Circle({
                strokeColor: '#FF0000',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: '#FF0000',
                fillOpacity: 0.35,
                map: this.map,
                center: {lat: latitude, lng: longitude},
                radius: radius
        });

        this.setCenter(latitude, Math.abs(longitude));
        this.currentCircle = circle;
    }

    removeCircle() {
        if (this.currentCircle) {
            this.currentCircle.setMap(null);
        }
    }

       /**
     * Handles the MileMaker route toggle click event
     * This function is responsible for:
     * 1. Fetching MileMaker route data if needed
     * 2. Drawing the route on the map when it's toggled ON
     * 3. Updating visibility when it's toggled ON/OFF
     * 4. Handling error states
     */
    public async handleMileMakerRouteToggle(stops: any[]) {
        const isRouteVisible = this.routeVisibility.milemaker;

        // Check if we have valid stops data
        if (!stops || !Array.isArray(stops) || stops.length < 2) {
            log.error('GoogleMap: Invalid or insufficient stops data for MileMaker route', stops);
            Snackbar.showWarningSnackbar("Not enough valid stops to create a MileMaker route");

            // Reset the toggle state
            this.routeVisibility.milemaker = false;

            // Update toggle button UI
            if (this.routeToggleButtons) {
                const options = this.getRouteToggleOptions();
                this.routeToggleButtons.updateOptions(options);
            }
            return;
        }

        if (isRouteVisible && !this.proposedRouteData) {
            try {
                log.debug(this, "Fetching MileMaker route data");

                // Make API call to fetch route
                const response = await Api.search("lme/dispatch/distance-lookup", {
                    stops: stops,
                    dist_profile: "fuel_dist_profile"
                });

                // Process response
                const mileMakerRouteData = Array.isArray(response.data) && response.data.length > 0
                    ? response.data[0].milemaker_route
                    : null;

                // Cache the route data for future use
                this.proposedRouteData = mileMakerRouteData;

                // Draw the route if data is available
                if (mileMakerRouteData) {
                    // Don't call full createRoute as it would reset other routes
                    // Just create the MileMaker route directly
                    if (this.milemakerPath) {
                        this.milemakerPath.setMap(null);
                    }

                    const milemakerColor = MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.MILEMAKER);
                    this.milemakerPath = new google.maps.Polyline({
                        path: mileMakerRouteData,
                        geodesic: true,
                        strokeColor: milemakerColor,
                        strokeOpacity: 1.0,
                        strokeWeight: 4
                    });

                    // Make visible since toggle is ON
                    this.milemakerPath.setMap(this.map);
                } else {
                    // No route data available
                    Snackbar.showWarningSnackbar("No MileMaker truck route available for this route");

                    // Update toggle state to reflect reality (route not available)
                    this.routeVisibility.milemaker = false;

                    // Update the toggle button UI
                    if (this.routeToggleButtons) {
                        // Create route toggle options based on current state
                        const syncOptions = this.getRouteToggleOptions();
                        this.routeToggleButtons.render({
                            ...syncOptions,
                            routeData: stops
                        });
                    }
                }
            } catch (error) {
                // Handle error state
                log.error(this, "Error fetching MileMaker route data", error);
                Snackbar.showWarningSnackbar("Failed to load MileMaker truck route");

                // Reset toggle state since we couldn't load the route
                this.routeVisibility.milemaker = false;

                // Update the toggle button UI
                if (this.routeToggleButtons) {
                    // Create route toggle options based on current state
                    const syncOptions = this.getRouteToggleOptions();
                    this.routeToggleButtons.render({
                        ...syncOptions,
                        routeData: stops
                    });
                }
            }
        }
        else if (this.milemakerPath) {
            this.milemakerPath.setVisible(isRouteVisible);
        }
    }

    /**
     * Handles the Trimble/PC*Miler route toggle click event
     * This function is responsible for:
     * 1. Fetching PC*Miler route data if needed
     * 2. Drawing the route on the map when it's toggled ON
     * 3. Updating visibility when it's toggled ON/OFF
     * 4. Handling error states
     */
    public async handleTrimbleRouteToggle(stops: any[]) {
        const isRouteVisible = this.routeVisibility.trimble;

        // Check if we have valid stops data
        if (!stops || !Array.isArray(stops) || stops.length < 2) {
            // Before showing error, check if we already have valid route data
            if (this.trimblePath && this.trimblePath.getPath().getLength() > 1) {
                // We have existing route data, just toggle visibility
                log.debug('GoogleMap: Using existing Trimble route data, toggling visibility to:', isRouteVisible);
                this.trimblePath.setMap(isRouteVisible ? this.map : null);
                return;
            }

            log.error('GoogleMap: Invalid or insufficient stops data for Trimble/PC*Miler route', stops);
            Snackbar.showWarningSnackbar("Not enough valid stops to create a PC*Miler route");

            // Reset the toggle state
            this.routeVisibility.trimble = false;

            // Update toggle button UI
            if (this.routeToggleButtons) {
                const options = this.getRouteToggleOptions();
                this.routeToggleButtons.updateOptions(options);
            }
            return;
        }

        // Check if we already have a valid path with data
        if (this.trimblePath && this.trimblePath.getPath().getLength() > 1) {
            // We have existing route data, just toggle visibility
            log.debug('GoogleMap: Using existing Trimble route data, toggling visibility to:', isRouteVisible);
            this.trimblePath.setMap(isRouteVisible ? this.map : null);
            return;
        }

        // Check if we need to fetch route data (either no path exists or path exists but has no data)
        const needToFetchRoute = isRouteVisible &&
            (!this.trimblePath ||
            (this.trimblePath && (!this.trimblePath.getPath() || this.trimblePath.getPath().getLength() <= 1)));

        if (needToFetchRoute) {
            try {
                log.debug(this, "Fetching PC*Miler route data");

                // Make API call to fetch route
                const response = await Api.search("lme/dispatch/distance-lookup", {
                    stops: stops,
                    dist_profile: "fuel_dist_profile"
                });

                // Process response
                const trimbleRouteData = Array.isArray(response.data) && response.data.length > 0
                    ? response.data[0].trimble_route
                    : null;

                // Save route data for future use
                if (trimbleRouteData && trimbleRouteData.length > 0) {
                    this.proposedRouteData = stops;
                }

                // Draw the route if data is available
                if (trimbleRouteData && trimbleRouteData.length > 0) {
                    // Don't call full createRoute as it would reset other routes
                    // Just create the Trimble route directly
                    if (this.trimblePath) {
                        this.trimblePath.setMap(null);
                    }

                    const trimbleColor = MapSettings.getSingleton().getRouteColor(MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.TRIMBLE);
                    this.trimblePath = new google.maps.Polyline({
                        path: trimbleRouteData,
                        geodesic: true,
                        strokeColor: trimbleColor,
                        strokeOpacity: 1.0,
                        strokeWeight: 4
                    });

                    // Make visible since toggle is ON
                    this.trimblePath.setMap(this.map);
                } else {
                    // No route data available
                    Snackbar.showWarningSnackbar("No PC*Miler truck route available for this route");

                    // Update toggle state to reflect reality (route not available)
                    this.routeVisibility.trimble = false;

                    // Update the toggle button UI
                    if (this.routeToggleButtons) {
                        // Create route toggle options based on current state
                        const syncOptions = this.getRouteToggleOptions();
                        this.routeToggleButtons.render({
                            ...syncOptions,
                            routeData: stops
                        });
                    }
                }
            } catch (error) {
                // Handle error state
                log.error(this, "Error fetching PC*Miler route data", error);
                Snackbar.showWarningSnackbar("Failed to load PC*Miler truck route");

                // Reset toggle state since we couldn't load the route
                this.routeVisibility.trimble = false;

                // Update the toggle button UI
                if (this.routeToggleButtons) {
                    // Create route toggle options based on current state
                    const syncOptions = this.getRouteToggleOptions();
                    this.routeToggleButtons.render({
                        ...syncOptions,
                        routeData: stops
                    });
                }
            }
        }
        else if (this.trimblePath || this.pcmilerOnPremPath) {
            // Toggle visibility of existing Trimble routes
            if (this.trimblePath) {
                this.trimblePath.setMap(isRouteVisible ? this.map : null);
            }
            if (this.pcmilerOnPremPath) {
                this.pcmilerOnPremPath.setMap(isRouteVisible ? this.map : null);
            }
        }
    }

    public setDistanceVendorInfo(vendorInfo: any): void {
        this.distanceVendorInfo = vendorInfo;
        log.debug('GoogleMap: Distance vendor info set:', vendorInfo);

        // Update toggle buttons if they exist
        if (this.routeToggleButtons && !this.disableToggleButtons) {
            const options = this.getRouteToggleOptions();
            this.routeToggleButtons.updateOptions(options);
        }
    }

    /**
     * Synchronous version of getRouteToggleOptions that uses cached vendor info
     * This method doesn't fetch new data from the API
     * @param routeData Optional route data to display
     * @param showProposedRoute Whether to show the proposed route
     * @param showGoogleRoute Whether to show the Google route (from user settings)
     * @param routeConfig Optional route configuration
     */
    private getRouteToggleOptions(routeData?: any[], showProposedRoute?: boolean, showGoogleRoute?: boolean, routeConfig?: any): RouteToggleOptions {
        // Make sure we're explicitly passing showProposedRoute value
        const config = routeConfig ?? this.distanceVendorInfo["routeConfig"] ?? null;
        const explicitShowProposedRoute = showProposedRoute !== undefined ? showProposedRoute : false;
        const hasGoogleRoute = !!this.routesAPIPath || !!this.directionsRenderer?.getDirections();
        const isGoogleRouteEnabled = showGoogleRoute ?? this.getDefaultShowGoogleRoute();

        const isMileMakerVendor = this.distanceVendorInfo?.isMileMaker === true;
        const isPcMilerVendor = this.distanceVendorInfo?.isPcMiler === true ||
                               (this.distanceVendorInfo?.source === 'A' || this.distanceVendorInfo?.source === 'M' ||
                                this.distanceVendorInfo?.source === 'T');

        const showMileMaker = (!!this.milemakerPath || isMileMakerVendor) &&
                              (config?.showMileMakerToggle !== false);

        // Trimble owns PC*Miler, so we'll show the Trimble toggle if there's a PC*Miler route
        const showTrimble = (!!this.trimblePath || !!this.pcmilerOnPremPath || isPcMilerVendor) &&
                            (config?.showTrimbleToggle !== false);

        const shouldShowGoogle = hasGoogleRoute || isGoogleRouteEnabled;

        log.debug('GoogleMap: getRouteToggleOptions - route info:', {
            googleRouteExists: hasGoogleRoute,
            milemakerPathExists: !!this.milemakerPath,
            trimblePathExists: !!this.trimblePath || !!this.pcmilerOnPremPath,
            showMileMakerToggle: showMileMaker,
            showTrimbleToggle: showTrimble,
            showProposedRoute: explicitShowProposedRoute,
            showGoogleRoute: showGoogleRoute
        });

        return {
            showGoogle: shouldShowGoogle,
            showTrimble: showTrimble,
            showMileMaker: showMileMaker,
            routeData: routeData || [],
            showProposedRoute: explicitShowProposedRoute
        };
    }

    private setGoogleRouteVisibility(visible: boolean) {
        // Update internal visibility state
        this.routeVisibility.google = visible;

        log.debug('GoogleMap: Setting Google route visibility to', visible);

        // Update the Polyline visibility if it exists
        if (this.routesAPIPath) {
            log.debug('GoogleMap: Updating RoutesAPI path visibility');
            this.routesAPIPath.setVisible(visible);
        }

        // Update the Directions renderer if it exists
        if (this.directionsRenderer) {
            log.debug('GoogleMap: Updating DirectionsRenderer visibility');
            this.directionsRenderer.setMap(visible ? this.map : null);
        }

        if (this.advanceMarkers && this.advanceMarkers.length > 0) {
            this.advanceMarkers.forEach(am => {
                if (am.type === RouteType.Google) {
                    am.marker.map = visible ? this.map : null;
                }
            });
        }
    }

    private setMileMakerRouteVisibility(visible: boolean) {
        // Update internal visibility state
        this.routeVisibility.milemaker = visible;

        log.debug('GoogleMap: Setting MileMaker route visibility to', visible);

        // Update the Polyline visibility if it exists
        if (this.milemakerPath) {
            this.milemakerPath.setMap(visible ? this.map : null);
        }
        this.milemakerRouteMarkers.forEach(marker => {
            marker.map = visible ? this.map : null;
        });
    }

    private setTrimbleRouteVisibility(visible: boolean) {
        // Update internal visibility state
        this.routeVisibility.trimble = visible;

        log.debug('GoogleMap: Setting Trimble route visibility to', visible);

        // Trimble owns both PC*Miler On Prem and PC*Miler Web
        if (this.trimblePath) {
            this.trimblePath.setMap(visible ? this.map : null);
        }
        // Also handle PC*Miler paths if needed
        if (this.pcmilerOnPremPath) {
            this.pcmilerOnPremPath.setMap(visible ? this.map : null);
        }
        this.trimbleRouteMarkers.forEach(marker => {
            marker.map = visible ? this.map : null;
        });
    }

    /**
     * Listen for fullscreen changes and re-append the toggle container
     */
    private setupFullscreenListener() {
        document.addEventListener('fullscreenchange', () => {
            // When entering or exiting fullscreen, recreate the toggle buttons
            if (this.routeToggleButtons) {
                // Need to recreate the toggle buttons since the DOM is different in fullscreen mode
                setTimeout(() => {
                    // Store current visibility states AND route data BEFORE destroying existing buttons
                    const currentVisibility = { ...this.routeVisibility };

                    // Save current route data for each route type
                    const savedRouteData = {
                        trimbleData: this.trimblePath ? this.trimblePath.getPath().getArray() : null,
                        milemakerData: this.milemakerPath ? this.milemakerPath.getPath().getArray() : null,
                        proposedRouteData: this.proposedRouteData
                    };

                    log.debug(this, "Fullscreen change detected, recreating route toggles with visibility:", currentVisibility);

                    // Get current toggle options
                    const options = this.getRouteToggleOptions();

                    // Preserve route data in options
                    if (options.routeData && options.routeData.length === 0 && this.proposedRouteData) {
                        options.routeData = this.proposedRouteData;
                    }

                    // Destroy existing toggles
                    this.routeToggleButtons.destroy();

                    // Create new toggle buttons with the current map div (which is now in/out of fullscreen)
                    const mapDiv = this.map.getDiv();
                    this.routeToggleButtons = new MapRouteToggleButtonsGoogle(
                        mapDiv,
                        currentVisibility, // Pass the stored visibility state
                        {
                            google: (visible) => this.setGoogleRouteVisibility(visible),
                            milemaker: (visible) => this.setMileMakerRouteVisibility(visible),
                            trimble: (visible) => this.setTrimbleRouteVisibility(visible),
                            handleRouteToggle: (rd) => this.handleMileMakerRouteToggle(rd || savedRouteData.proposedRouteData),
                            handleTrimbleRouteToggle: (rd) => this.handleTrimbleRouteToggle(rd || savedRouteData.proposedRouteData)
                        }
                    );

                    // Preserve the _userToggled flag to ensure buttons show the correct state
                    options._userToggled = true;

                    // Render the buttons with the current options
                    this.routeToggleButtons.render(options);

                    // Restore any existing path data that may have been lost in the transition
                    if (currentVisibility.trimble && savedRouteData.trimbleData && !this.trimblePath) {
                        const trimbleColor = MapSettings.getSingleton().getRouteColor(
                            MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.TRIMBLE);
                        this.trimblePath = new google.maps.Polyline({
                            path: savedRouteData.trimbleData,
                            geodesic: true,
                            strokeColor: trimbleColor,
                            strokeOpacity: 1.0,
                            strokeWeight: 4
                        });
                        this.trimblePath.setMap(this.map);
                    }

                    if (currentVisibility.milemaker && savedRouteData.milemakerData && !this.milemakerPath) {
                        const milemakerColor = MapSettings.getSingleton().getRouteColor(
                            MapSettings.MAP_VENDOR.GOOGLE, MapSettings.ROUTE_VENDOR.MILEMAKER);
                        this.milemakerPath = new google.maps.Polyline({
                            path: savedRouteData.milemakerData,
                            geodesic: true,
                            strokeColor: milemakerColor,
                            strokeOpacity: 1.0,
                            strokeWeight: 4
                        });
                        this.milemakerPath.setMap(this.map);
                    }

                    // Restore proposedRouteData if it was lost
                    if (!this.proposedRouteData && savedRouteData.proposedRouteData) {
                        this.proposedRouteData = savedRouteData.proposedRouteData;
                    }
                }, 300); // Short delay to allow DOM to settle
            }
        });
    }

    isStyleLoaded: () => boolean;

    onStyleLoad: (callback: () => void) => void;

}
